<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-evaluation orangehrm-card-container">
    <div
      class="orangehrm-evaluation-card-head"
      :class="{'orangehrm-evaluation-card-head--collapsible': collapsible}"
      @click="onHeadClick"
    >
      <oxd-text class="orangehrm-main-title">
        {{ title }}
      </oxd-text>

      <div class="orangehrm-evaluation-header">
        <oxd-grid :cols="3" class="orangehrm-evaluation-header-grid">
          <oxd-grid-item class="orangehrm-evaluation-title">
            <img
              class="orangehrm-evaluation-title-profile-image"
              alt="profile picture"
              :src="profileImgSrc"
            />
            <div class="orangehrm-evaluation-title-name">
              <oxd-text type="card-title">
                {{ employeeName }}
              </oxd-text>
              <oxd-text type="card-body">
                {{ jobTitle }}
              </oxd-text>
            </div>
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-text type="card-body">
              {{ $t('general.status') }}
            </oxd-text>
            <oxd-text type="card-title">
              {{ evaluationLabel }}
            </oxd-text>
          </oxd-grid-item>
        </oxd-grid>
        <oxd-icon-button
          v-if="collapsible"
          :with-container="false"
          :name="isCollapsed ? 'chevron-down' : 'chevron-up'"
          @click.stop="toggleForm"
        />
      </div>
    </div>

    <template v-if="!isCollapsed">
      <oxd-divider />

      <!-- KPIs if grouping true -->
      <template v-if="kpiCategories && kpiCategories.length > 0">
        <div
          v-for="(category, categoryIndex) in kpiCategories"
          :key="category.categoryId"
          class="orangehrm-evaluation-category"
        >
          <div class="orangehrm-evaluation-category-header">
            <div class="orangehrm-evaluation-category-header__content">
              <span class="orangehrm-evaluation-category-header__eyebrow">
                {{ $t('performance.kpi_categories_summary') }}
              </span>
              <oxd-text
                type="subtitle-1"
                class="orangehrm-evaluation-category-title"
              >
                {{ category.categoryName }}
              </oxd-text>
            </div>
            <div class="orangehrm-evaluation-category-header__meta">
              <span
                v-if="category.weight != null"
                class="orangehrm-evaluation-category-badge"
              >
                {{ $t('performance.kpi_weight') }}: {{ category.weight }}%
              </span>
              <span
                class="orangehrm-evaluation-category-badge orangehrm-evaluation-category-badge--muted"
              >
                {{ countCategoryKpis(category) }} {{ $t('general.kpis') }}
              </span>
            </div>
          </div>

          <div
            v-for="subCategory in category.subCategory"
            :key="subCategory.subCategoryId"
            class="orangehrm-evaluation-subcategory"
          >
            <div class="orangehrm-evaluation-subcategory-header">
              <div class="orangehrm-evaluation-subcategory-header__content">
                <span class="orangehrm-evaluation-subcategory-header__eyebrow">
                  {{ $t('performance.kpi_sub_categories_name') }}
                </span>
                <oxd-text
                  type="subtitle-2"
                  class="orangehrm-evaluation-subcategory-title"
                >
                  {{ subCategory.subCategoryName }}
                </oxd-text>
              </div>
              <span class="orangehrm-evaluation-subcategory-count">
                {{ subCategory.kpi?.length || 0 }} {{ $t('general.kpis') }}
              </span>
            </div>

            <div class="orangehrm-evaluation-kpi-table">
              <div class="orangehrm-evaluation-kpi-table__head">
                <div>{{ $t('general.kpis') }}</div>
                <div>{{ $t('performance.rating') }}</div>
                <div>{{ $t('general.comments') }}</div>
              </div>

              <div
                v-for="kpi in subCategory.kpi"
                :key="kpi.id"
                class="orangehrm-evaluation-kpi-row"
              >
                <div class="orangehrm-evaluation-grid-kpi">
                  <oxd-text
                    class="orangehrm-evaluation-grid-kpi-header"
                    type="subtitle-2"
                  >
                    {{ $t('performance.kpi') }}
                  </oxd-text>
                  <oxd-text
                    :title="kpi.title"
                    tag="p"
                    class="orangehrm-evaluation-grid-kpi-label"
                  >
                    {{ kpi.title }}
                  </oxd-text>
                  <oxd-text
                    v-if="kpi.description"
                    class="orangehrm-evaluation-grid-kpi-description"
                    tag="p"
                  >
                    {{ kpi.description }}
                  </oxd-text>
                  <div class="orangehrm-evaluation-grid-kpi-range">
                    <span
                      >{{ $t('performance.min') }}: {{ kpi.minRating }}</span
                    >
                    <span
                      >{{ $t('performance.max') }}: {{ kpi.maxRating }}</span
                    >
                  </div>
                </div>
                <div class="orangehrm-evaluation-kpi-row__rating">
                  <oxd-text
                    class="orangehrm-evaluation-grid-kpi-header"
                    type="subtitle-2"
                  >
                    {{ $t('performance.rating') }}
                  </oxd-text>
                  <oxd-input-field
                    type="input"
                    :disabled="!editable"
                    :rules="getRulesForKpi(kpi.id)"
                    :model-value="getRatingForKpi(kpi.id)"
                    @update:model-value="onUpdateRating($event, kpi.id)"
                  />
                </div>
                <div class="orangehrm-evaluation-kpi-row__comment">
                  <oxd-text
                    class="orangehrm-evaluation-grid-kpi-header"
                    type="subtitle-2"
                  >
                    {{ $t('general.comment') }}
                  </oxd-text>
                  <oxd-input-field
                    class="orangehrm-evaluation-grid-comment"
                    rows="2"
                    type="textarea"
                    :disabled="!editable"
                    :rules="commentValidators"
                    :model-value="getCommentForKpi(kpi.id)"
                    @update:model-value="onUpdateComment($event, kpi.id)"
                  />
                </div>
              </div>
            </div>
          </div>

          <oxd-divider
            v-if="categoryIndex < kpiCategories.length - 1"
            class="orangehrm-evaluation-category-divider"
          />
        </div>
      </template>

      <!--KPIs if grouping false -->
      <template v-else>
        <oxd-grid :cols="4" class="orangehrm-evaluation-grid">
          <oxd-grid-item class="orangehrm-evaluation-grid-header">
            <oxd-text type="subtitle-2">{{ $t('general.kpis') }}</oxd-text>
          </oxd-grid-item>
          <oxd-grid-item class="orangehrm-evaluation-grid-header">
            <oxd-text type="subtitle-2"
              >{{ $t('performance.rating') }}
            </oxd-text>
          </oxd-grid-item>
          <oxd-grid-item class="orangehrm-evaluation-grid-header">
            <oxd-text type="subtitle-2">{{ $t('general.comments') }}</oxd-text>
          </oxd-grid-item>
          <oxd-grid-item
            class="orangehrm-evaluation-grid-spacer-md"
          ></oxd-grid-item>

          <template v-for="(kpi, index) in kpis" :key="kpi.id">
            <oxd-grid-item class="orangehrm-evaluation-grid-kpi">
              <oxd-text
                class="orangehrm-evaluation-grid-kpi-header"
                type="subtitle-2"
              >
                {{ $t('performance.kpi') }}
              </oxd-text>
              <oxd-text
                :title="kpi.title"
                tag="p"
                class="orangehrm-evaluation-grid-kpi-label"
              >
                {{ kpi.title }}
              </oxd-text>
              <oxd-text
                v-if="kpi.description"
                class="orangehrm-evaluation-grid-kpi-description"
                tag="p"
              >
                {{ kpi.description }}
              </oxd-text>
              <oxd-text class="orangehrm-evaluation-grid-kpi-minmax" tag="p">
                {{ $t('performance.min') }}: {{ kpi.minRating }}
              </oxd-text>
              <oxd-text class="orangehrm-evaluation-grid-kpi-minmax" tag="p">
                {{ $t('performance.max') }}: {{ kpi.maxRating }}
              </oxd-text>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-text
                class="orangehrm-evaluation-grid-kpi-header"
                type="subtitle-2"
              >
                {{ $t('performance.rating') }}
              </oxd-text>
              <oxd-input-field
                type="input"
                :disabled="!editable"
                :rules="rules[index]"
                :model-value="getRatingForKpi(kpi.id)"
                @update:model-value="onUpdateRatingByIndex($event, index)"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-text
                class="orangehrm-evaluation-grid-kpi-header"
                type="subtitle-2"
              >
                {{ $t('general.comment') }}
              </oxd-text>
              <oxd-input-field
                class="orangehrm-evaluation-grid-comment"
                rows="2"
                type="textarea"
                :disabled="!editable"
                :rules="commentValidators"
                :model-value="modelValue.kpis[index].comment"
                @update:model-value="onUpdateCommentByIndex($event, index)"
              />
            </oxd-grid-item>
            <oxd-grid-item
              class="orangehrm-evaluation-grid-spacer-md"
            ></oxd-grid-item>
          </template>
        </oxd-grid>
      </template>

      <oxd-divider />
      <div class="orangehrm-evaluation-general-comment">
        <oxd-text tag="p" class="orangehrm-evaluation-general-comment-label">
          {{ $t('performance.general_comment') }}
        </oxd-text>
        <oxd-input-field
          class="orangehrm-evaluation-general-comment-input"
          rows="3"
          type="textarea"
          :disabled="!editable"
          :rules="commentValidators"
          :model-value="modelValue.generalComment"
          @update:model-value="onUpdateGeneralComment($event)"
        />
      </div>
      <slot></slot>
    </template>
  </div>
</template>

<script>
import {computed, ref, watch} from 'vue';
import usei18n from '@/core/util/composable/usei18n';
import {shouldNotExceedCharLength} from '@/core/util/validation/rules';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import {OxdDivider} from '@ohrm/oxd';

const defaultPic = `${window.appGlobal.publicPath}/images/default-photo.png`;

export default {
  components: {
    'oxd-divider': OxdDivider,
  },
  props: {
    kpis: {
      type: Array,
      required: true,
    },
    kpiCategories: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      required: true,
    },
    editable: {
      type: Boolean,
      required: true,
    },
    collapsible: {
      type: Boolean,
      required: true,
    },
    employee: {
      type: Object,
      required: true,
    },
    jobTitle: {
      type: String,
      required: true,
    },
    rules: {
      type: Array,
      required: true,
    },
    modelValue: {
      type: Object,
      required: true,
      validator: (value) =>
        Object.hasOwn(value, 'kpis') && Object.hasOwn(value, 'generalComment'),
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
    status: {
      type: Number,
      required: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, context) {
    const {$t} = usei18n();
    const {$tEmpName} = useEmployeeNameTranslate();
    const isCollapsed = ref(props.collapsible && props.collapsed);
    const commentValidators = [shouldNotExceedCharLength(2000)];

    watch(
      () => [props.collapsed, props.collapsible],
      ([collapsed, collapsible]) => {
        isCollapsed.value = collapsible ? collapsed : false;
      },
    );

    const profileImgSrc = computed(() => {
      return props.employee.empNumber
        ? `${window.appGlobal.baseUrl}/pim/viewPhoto/empNumber/${props.employee.empNumber}`
        : defaultPic;
    });

    const employeeName = computed(() => {
      return $tEmpName(props.employee);
    });

    const toggleForm = () => {
      isCollapsed.value = !isCollapsed.value;
    };

    const onHeadClick = () => {
      if (props.collapsible) {
        toggleForm();
      }
    };

    const findKpiIndexById = (kpiId) => {
      return props.modelValue.kpis.findIndex((item) => item.kpiId === kpiId);
    };

    const findKpiRuleIndexById = (kpiId) => {
      return props.kpis.findIndex((kpi) => kpi.id === kpiId);
    };

    const getRulesForKpi = (kpiId) => {
      const index = findKpiRuleIndexById(kpiId);
      return index !== -1 ? props.rules[index] : [];
    };

    const normalizeRatingValue = (value) => {
      if (value === '' || value === null || value === undefined) {
        return null;
      }

      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    const getRatingForKpi = (kpiId) => {
      const index = findKpiIndexById(kpiId);
      if (index === -1) {
        return null;
      }

      return normalizeRatingValue(props.modelValue.kpis[index].rating);
    };

    const getCommentForKpi = (kpiId) => {
      const index = findKpiIndexById(kpiId);
      return index !== -1 ? props.modelValue.kpis[index].comment : null;
    };

    const onUpdateRating = (value, kpiId) => {
      const index = findKpiIndexById(kpiId);
      if (index !== -1) {
        const updatedKpis = [...props.modelValue.kpis];
        updatedKpis[index] = {
          ...updatedKpis[index],
          rating: normalizeRatingValue(value),
        };

        context.emit('update:modelValue', {
          kpis: updatedKpis,
          generalComment: props.modelValue.generalComment,
        });
      }
    };

    const onUpdateComment = (value, kpiId) => {
      const index = findKpiIndexById(kpiId);
      if (index !== -1) {
        const updatedKpis = [...props.modelValue.kpis];
        updatedKpis[index] = {...updatedKpis[index], comment: value};

        context.emit('update:modelValue', {
          kpis: updatedKpis,
          generalComment: props.modelValue.generalComment,
        });
      }
    };

    const onUpdateRatingByIndex = (value, index) => {
      const updatedKpis = [...props.modelValue.kpis];
      updatedKpis[index] = {
        ...updatedKpis[index],
        rating: normalizeRatingValue(value),
      };

      context.emit('update:modelValue', {
        kpis: updatedKpis,
        generalComment: props.modelValue.generalComment,
      });
    };

    const onUpdateCommentByIndex = (value, index) => {
      const updatedKpis = [...props.modelValue.kpis];
      updatedKpis[index] = {...updatedKpis[index], comment: value};

      context.emit('update:modelValue', {
        kpis: updatedKpis,
        generalComment: props.modelValue.generalComment,
      });
    };

    const onUpdateGeneralComment = (value) => {
      context.emit('update:modelValue', {
        kpis: props.modelValue.kpis,
        generalComment: value,
      });
    };

    const statusOpts = [
      {id: 1, label: $t('performance.evaluation_activated')},
      {id: 2, label: $t('performance.evaluation_in_progress')},
      {id: 3, label: $t('performance.evaluation_completed')},
    ];

    const evaluationLabel = computed(
      () => statusOpts.find((el) => el.id === props.status)?.label || '',
    );

    const countCategoryKpis = (category) => {
      if (!category?.subCategory) {
        return 0;
      }

      return category.subCategory.reduce((total, subCategory) => {
        return total + (subCategory.kpi?.length || 0);
      }, 0);
    };

    return {
      toggleForm,
      isCollapsed,
      employeeName,
      profileImgSrc,
      onUpdateRating,
      onUpdateComment,
      onUpdateRatingByIndex,
      onUpdateCommentByIndex,
      onUpdateGeneralComment,
      commentValidators,
      evaluationLabel,
      getRulesForKpi,
      getRatingForKpi,
      getCommentForKpi,
      countCategoryKpis,
      onHeadClick,
    };
  },
};
</script>

<style src="./evaluation-form.scss" lang="scss" scoped></style>
